import { useTweaks } from '../components/Tweaks.jsx';
import { useOfferTweakDefaults } from '../offers/OfferContext.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { PricingHero, PricingIncluded, PricingPromise, PricingFAQ, PricingGift } from '../components/Pricing.jsx';
import { IntensiveCrossLink } from '../components/IntensiveCrossLink.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',
  pricingHeroKicker: 'Pricing',
  pricingHeroTitle: 'How much does SaySomethingin cost?',
  pricingHeroLede: 'One subscription. You get every language we build. No tricks, no add-ons, no upsells – just a fair price for high intensity speaking exercises that will transform your language learning.',
  planMonthlyName: 'Monthly',
  planMonthlyBlurb: 'The flexible option. Cancel any time, no questions.',
  planMonthlyCta: 'Try 7 days free',
  planAnnualName: 'Annual',
  planAnnualBlurb: 'The committed option. Two months free when you go yearly.',
  planAnnualCompare: "That's £12.50 / month – two months free",
  planAnnualCta: 'Try 7 days free',
  planAnnualExtra: 'Annual price locked for the lifetime of your subscription',
  planSaveBadge: 'Save £30',
  planMonthlyNum: '15',
  planMonthlyPer: '/ month',
  planAnnualNum: '150',
  planAnnualPer: '/ year',
  planLine1: 'Every language in the app – not a per-course upgrade',
  planLine2: '7 days free to try. No card charged until day 8.',
  planLine3: 'Cancel any time from your account settings',
  trialStripStrong: '7 days free',
  trialStripRest: "on both plans. Full access, no limits, no card charged until day 8. If it's not for you, cancel – we'll never surprise you with a bill.",
  inclKicker: "What's included",
  inclTitle: 'Eleven languages. One price.',
  inclLede: "Your subscription unlocks every course we've built. Start with one, dabble in a few, or switch languages when the mood takes you – it's all the same fee.",
  promiseKicker: 'Our promise',
  promiseTitle: 'We never raise the price on existing subscribers.',
  promiseBody: "We're always improving the app, and we do sometimes increase the price for new subscribers. But if you sign up today at £15 a month or £150 a year, that's your price – locked in for as long as you keep your subscription going. If your price ever goes up, it'll be because you cancelled and came back.",
  pfaqKicker: 'The money bit',
  pfaqTitle: 'Pricing questions, straight answers.',
  pfaqQ1: 'Is the 7-day trial really free?',
  pfaqA1: "Yes. Full access to every course for 7 days, no card charged until day 8. If you cancel before then, you pay nothing. No 'oops' first-month charge.",
  pfaqQ2: 'Can I cancel any time?',
  pfaqA2: 'Yes, from your account settings. Monthly subscribers stop billing at the end of the current month. Annual subscribers keep access until the end of the year they paid for; no partial refund, but no surprise re-bills either.',
  pfaqQ3: 'Do I get all the languages, or just one?',
  pfaqA3: "All of them. One subscription, every course we've built – Welsh, Spanish, French, Italian, German, Portuguese, Japanese, Korean, Mandarin Chinese, Arabic, and English as a second language.",
  pfaqQ4: "What if it doesn't work for me?",
  pfaqA4: "Email us within the first month, tell us you put the work in and it didn't click, and we'll refund you. We'd rather have your honest feedback than your reluctant money.",
  pfaqQ5: 'Will my price go up later?',
  pfaqA5: 'No. We sometimes raise prices for new subscribers, but never for existing ones. The price you sign up at is the price you keep, for as long as your subscription runs uninterrupted.',
  pgiftKicker: 'Buying for someone else?',
  pgiftTitle: 'Give a year of SaySomethingin',
  pgiftBody: 'A gift voucher unlocks 12 months of full access, for the price of 10 – and arrives with a note from you.',
  pgiftCta: 'See gift vouchers',
  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero + plans', keys: ['pricingHeroKicker','pricingHeroTitle','pricingHeroLede','planMonthlyName','planMonthlyBlurb','planMonthlyCta','planAnnualName','planAnnualBlurb','planAnnualCompare','planAnnualCta','planAnnualExtra','planSaveBadge','planLine1','planLine2','planLine3','trialStripStrong','trialStripRest'], long:['pricingHeroLede','trialStripRest'] },
  { name: 'Included', keys: ['inclKicker','inclTitle','inclLede'], long:['inclLede'] },
  { name: 'Promise', keys: ['promiseKicker','promiseTitle','promiseBody'], long:['promiseBody'] },
  { name: 'FAQ', keys: ['pfaqKicker','pfaqTitle','pfaqQ1','pfaqA1','pfaqQ2','pfaqA2','pfaqQ3','pfaqA3','pfaqQ4','pfaqA4','pfaqQ5','pfaqA5'], long:['pfaqA1','pfaqA2','pfaqA3','pfaqA4','pfaqA5'] },
  { name: 'Gift cross-link', keys: ['pgiftKicker','pgiftTitle','pgiftBody','pgiftCta'], long:['pgiftBody'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Pricing() {
  const defaults = useOfferTweakDefaults(TWEAK_DEFAULTS, 'pricing');
  const { tweaks, panel } = useTweaks({ defaults, groups: GROUPS });
  return (
    <div data-screen-label="Pricing">
      <Header t={tweaks}/>
      <main>
        <PricingHero t={tweaks}/>
        <PricingIncluded t={tweaks}/>
        <PricingPromise t={tweaks}/>
        <IntensiveCrossLink variant="pricing"/>
        <PricingFAQ t={tweaks}/>
        {/* <PricingGift t={tweaks}/> */}
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
